import { useCallback, useMemo } from "react";
import {
  Bank,
  BookSaved,
  Briefcase,
  Call,
  DocumentText,
  Location,
  Note,
  Profile,
  People,
  Calendar,
  TickCircle,
  type Icon,
} from "iconsax-react";
import { Link } from "react-router";
import { Loader } from "@/shared/components";
import { useAppSelector } from "@/shared/hooks";
import { Button, Prompt, PromptForm, StepsSwitcher } from "@/shared/ui";
import { useApplyApplicationPage, type SectionKey } from "@/features/hr/hiring";
import { formatDateForDisplay } from "@/shared/utils";
import SectionRenderer from "./onboarding/SectionRenderer";

export default function ApplyApplicationPage() {
  const {
    token,
    active,
    setActive,
    showConfirm,
    setShowConfirm,
    showPrompt,
    setShowPrompt,
    isSubmitted,
    isChief,
    submitResponse,
    validationData,
    validationLoading,
    validationError,
    completenessLoadError,
    submitIsLoading,
    disabledSteps,
    completedSteps,
    handleSubmit,
    t,
  } = useApplyApplicationPage();

  const isCompleted = useAppSelector((s) => s.completeness.data?.is_complete);

  const sectionOrder = useMemo(() => {
    const base: SectionKey[] = [
      "personal_data",
      "contacts",
      "addresses",
      "id_documents",
      "banking",
      "education",
      "experience",
    ];
    if (isChief) base.push("bessrochnyi_contract");
    base.push("social_categories");
    return base;
  }, [isChief]);

  const sectionsWithIcons = useMemo(() => {
    const base: { id: SectionKey; icon: Icon }[] = [
      { id: "personal_data", icon: Profile },
      { id: "contacts", icon: Call },
      { id: "addresses", icon: Location },
      { id: "id_documents", icon: Note },
      { id: "banking", icon: Bank },
      { id: "education", icon: BookSaved },
      { id: "experience", icon: Briefcase },
    ];
    if (isChief) {
      base.push({ id: "bessrochnyi_contract", icon: DocumentText });
    }
    base.push({ id: "social_categories", icon: People });
    return base;
  }, [isChief]);

  const handleNext = useCallback(
    function handleNext() {
      const currentIndex = sectionOrder.indexOf(active);
      if (currentIndex < sectionOrder.length - 1) {
        setActive(sectionOrder[currentIndex + 1]);
      }
    },
    [active, setActive, sectionOrder],
  );

  if (!token) return null;

  if (validationLoading) {
    return <Loader isFullPage />;
  }

  if (validationError || !validationData?.valid) {
    return (
      <div className="max-w-3xl mx-auto my-16">
        <h1 className="text-display-xs content-base-primary mb-4">{t("page.invalid_link")}</h1>
        <p className="text-body-md content-base-secondary">{validationData?.error ?? t("page.link_expired")}</p>
      </div>
    );
  }

  if (isSubmitted) {
    const isChiefDone = submitResponse?.application_type === "chief_onboarding";

    return (
      <>
        <title>{isChiefDone ? t("page.chief_title") : t("page.title")}</title>
        <div className="max-w-3xl mx-auto my-16 flex flex-col items-center text-center gap-4">
          <TickCircle size={96} color="currentColor" variant="Bold" />
          <h1 className="text-display-xs content-base-primary">
            {isChiefDone ? t("page.chief_success_title") : t("page.success_title")}
          </h1>
          <p className="text-body-md content-base-secondary">
            {isChiefDone
              ? t("page.chief_success_message", { email: submitResponse?.email ?? "" })
              : t("page.success_message")}
          </p>
          {isChiefDone && (
            <Link to="/login" className="text-body-bold-md content-action-brand mt-4">
              {t("page.go_to_login")}
            </Link>
          )}
        </div>
      </>
    );
  }

  if (completenessLoadError) {
    return (
      <div className="max-w-3xl mx-auto my-16">
        <h1 className="text-display-xs content-base-primary mb-4">{t("page.error_title")}</h1>
        <p className="text-body-md content-base-secondary">{t(completenessLoadError)}</p>
      </div>
    );
  }

  const showRevisionNotes = validationData?.status === "revision_requested" && validationData?.revision_notes;

  return (
    <>
      <title>{isChief ? t("page.chief_title") : t("page.title")}</title>
      <div className="w-full xl:w-7xl mx-auto flex flex-col gap-7 pb-16 px-4 xl:px-0">
        <header className="flex flex-col gap-2">
          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col gap-2">
              <h1 className="text-display-sm content-base-primary">
                {isChief ? t("page.chief_heading") : t("page.heading")}
              </h1>
              <p className="text-body-regular-md content-base-secondary">
                {isChief ? t("page.chief_subtitle") : t("page.subtitle")}
              </p>
              {isChief && validationData?.organization && (
                <p className="text-body-regular-sm content-base-low">
                  {t("page.chief_org_context", {
                    name: validationData.organization.name,
                    bin: validationData.organization.bin,
                  })}
                </p>
              )}
            </div>
            <div className="w-fit shrink-0">
              <Button variant="primary" size="huge" disabled={!isCompleted} textDuringDisabled={!isCompleted ? t("page.rules.incomplete") : undefined} fullWidthTooltip onClick={() => setShowConfirm(true)}>
                {t("page.submit_review")}
              </Button>
            </div>
          </div>
          {showRevisionNotes && (
            <div className="mt-4 p-4 rounded-lg border surface-base-stroke surface-base-fill">
              <h3 className="text-body-bold-md content-base-primary mb-2 pb-2 border-b surface-base-stroke">
                {t("page.revision_requested_title")}
              </h3>
              <p className="text-body-regular-md content-base-secondary whitespace-pre-wrap">
                {validationData.revision_notes}
              </p>
              {validationData.revision_requested_at && (
                <div className="flex justify-start items-center gap-2 mt-3">
                  <span className="w-4 h-4 flex justify-center items-center">
                    <Calendar size={16} color="currentColor" />
                  </span>
                  <p className="text-body-regular-xs content-base-primary">
                    {t("page.revision_requested_at")}:{" "}
                    {formatDateForDisplay(validationData.revision_requested_at, true)}
                  </p>
                </div>
              )}
            </div>
          )}
        </header>

        <StepsSwitcher
          steps={sectionsWithIcons.map((section) => ({
            id: section.id,
            label: t(`sections.${section.id}`),
            icon: section.icon,
          }))}
          activeStep={active}
          onStepChange={(stepId) => {
            if (disabledSteps.includes(stepId as typeof active)) return;
            setActive(stepId as typeof active);
          }}
          scrollToTopOnChange
          disabledSteps={disabledSteps}
          disabledTooltip={t("sections.personal_data_required_tooltip")}
          completedSteps={completedSteps}>
          <SectionRenderer section={active} token={token} onNext={handleNext} isChief={isChief} />
        </StepsSwitcher>

        {showConfirm && (
          <PromptForm
            variant="warning"
            onClose={() => setShowConfirm(false)}
            onConfirm={handleSubmit}
            title={t("prompt_form.title")}
            text={t("prompt_form.description")}
            isLoading={submitIsLoading}
          />
        )}

        {showPrompt.type === "error" && (
          <Prompt
            variant="error"
            onClose={() => setShowPrompt({ type: null })}
            title={t("page.error_title")}
            text={showPrompt.text ?? t("page.error_message")}
          />
        )}
      </div>
    </>
  );
}
